import {z} from 'zod';

import {PHONE_NUMBER_REGEX} from '../utils/validation';

const REQUIRED_FIELD_MESSAGE = 'This is a required field';

// ============================================================================
// ADDRESS SCHEMA (Single source of truth)
// ============================================================================

/**
 * Address object schema - validates final address regardless of input method
 * Used by: AddressModal, DeliveryAddress, BillingAddress
 */
export const addressSchema = z
  .object({
    lines: z.array(z.string()).min(1, 'Street address is required'),
    locality: z.string().min(1, 'Suburb is required'),
    administrative_area: z.string().min(1, 'State is required'),
    postal_code: z.string().min(1, 'Postcode is required'),
  })
  .nullable();

export type AddressData = z.infer<typeof addressSchema>;

/**
 * AddressModal form schema - wraps address in form structure
 */
export const addressModalSchema = z.object({
  address: addressSchema.refine(val => val !== null, {
    message: 'Address is required',
  }),
});

export type AddressModalFormData = z.infer<typeof addressModalSchema>;

/**
 * ManualAddressEntry field schema - internal validation only
 * Transforms to Address object on submit
 *
 * MFL format: lines[0] = unit/apartment, lines[1] = street address
 * - line1 = Unit/Apartment (optional) → stored as lines[0]
 * - line2 = Street address (required) → stored as lines[1]
 */
export const manualAddressFieldsSchema = z.object({
  line1: z.string().trim().optional(), // Unit/Apartment (optional), trimmed
  line2: z.string().trim().min(1, 'Street address is required'), // Street address (required), trimmed
  locality: z.string().trim().min(1, 'Suburb is required'),
  postal_code: z.string().trim().min(1, 'Postcode is required'),
  administrative_area: z.string().trim().min(1, 'State is required'),
});

export type ManualAddressFieldsFormData = z.infer<typeof manualAddressFieldsSchema>;

// ============================================================================
// PERSON ACCEPTING ORDER SCHEMA (Conditional Validation)
// ============================================================================

/**
 * AcceptOrder schema with conditional validation
 * firstName/lastName/mobile only required when person === 'someone_else'
 */
export const acceptOrderSchema = z
  .object({
    person: z.enum(['me', 'someone_else']),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    mobile: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.person === 'someone_else') {
      if (!data.firstName || data.firstName.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['firstName'],
          message: REQUIRED_FIELD_MESSAGE,
        });
      }
      if (!data.lastName || data.lastName.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['lastName'],
          message: REQUIRED_FIELD_MESSAGE,
        });
      }
      if (!data.mobile || data.mobile.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['mobile'],
          message: REQUIRED_FIELD_MESSAGE,
        });
      } else if (!PHONE_NUMBER_REGEX.test(data.mobile)) {
        ctx.addIssue({
          code: 'custom',
          path: ['mobile'],
          message: 'Please enter a valid Australian phone number',
        });
      }
    }
  });

export type AcceptOrderFormData = z.infer<typeof acceptOrderSchema>;

// ============================================================================
// SLOT SELECTION SCHEMA (Nested Conditional)
// ============================================================================

/**
 * Slot object schema - matches CartStore selectedSlot type
 */
export const slotObjectSchema = z
  .object({
    id: z.string(),
  })
  .passthrough(); // Allow additional properties from API

/**
 * SlotModal schema - day required, slot required when day is selected
 */
export const slotSchema = z
  .object({
    selectedDay: z.string().nullable(),
    selectedSlot: slotObjectSchema.nullable(),
  })
  .superRefine((data, ctx) => {
    if (!data.selectedDay) {
      ctx.addIssue({
        code: 'custom',
        path: ['selectedDay'],
        message: 'Please select a day',
      });
    }
    if (data.selectedDay && !data.selectedSlot) {
      ctx.addIssue({
        code: 'custom',
        path: ['selectedSlot'],
        message: 'Please select a time',
      });
    }
  });

export type SlotFormData = z.infer<typeof slotSchema>;

// ============================================================================
// ORDER NOTES SCHEMA
// ============================================================================

const ORDER_NOTES_MAX_LENGTH = 500;

export const orderNotesSchema = z.object({
  notes: z
    .string()
    .max(ORDER_NOTES_MAX_LENGTH, `Notes cannot exceed ${ORDER_NOTES_MAX_LENGTH} characters`)
    .optional()
    .default(''),
});

export type OrderNotesFormData = z.infer<typeof orderNotesSchema>;

export {ORDER_NOTES_MAX_LENGTH, REQUIRED_FIELD_MESSAGE};
